import { validateTotals } from '@/lib/validators/nutritionValidators'
import {
  DailyNutrition,
  FoodItem,
  Meal,
  MealCategory,
  MealCategoryInput,
  NutritionalValue,
  TimeRange,
} from '@/types/nutrition'
import {
  defaultParserConfig,
  INutritionParser,
  InvalidDateError,
  InvalidFoodItemError,
  MissingTotalsError,
  NutritionParseError,
  ParsedMeal,
  ParseFailure,
  ParserConfig,
  ParseResult,
  ParseSuccess,
} from '@/types/parser'
import { FoodItemParser } from './FoodItemParser'
import { MarkdownTableParser } from './MarkdownTableParser'

/**
 * Main parser class for processing nutrition data from text format.
 *
 * Handles complex nutrition data parsing including food descriptions with brands,
 * various units, meal categorization, and comprehensive validation.
 *
 * @example
 * ```typescript
 * const parser = new NutritionDataParser()
 * const result = parser.parse(nutritionData)
 *
 * if (result.success) {
 *   console.log('Total calories:', result.data.totals.calories)
 * }
 * ```
 */
export class NutritionDataParser implements INutritionParser {
  private foodItemParser = new FoodItemParser()
  private markdownTableParser = new MarkdownTableParser()
  private readonly mealCategories: readonly MealCategory[] = [
    'Breakfast',
    'Lunch',
    'Dinner',
    'Late meal',
  ] as const

  /**
   * Checks if a line represents a meal category (standard or time-based).
   * Supports both standard meal names and time-based categories like "00-04", "16-20", "20-24".
   *
   * @param line - The line to check
   * @returns The meal category if found, undefined otherwise
   */
  private identifyMealCategory(line: string): MealCategoryInput | undefined {
    // Check standard meal categories first
    const standardCategory = this.mealCategories.find((category) => line.trim() === category)
    if (standardCategory) {
      return standardCategory
    }

    // Check for time-based meal categories (e.g., "00-04", "16-20", "20-24")
    const timePattern = /^\d{2}-\d{2}$/
    if (timePattern.test(line.trim())) {
      return line.trim() as TimeRange
    }

    return undefined
  }

  /**
   * Parses raw nutrition data string into structured format.
   *
   * @param rawData - Raw nutrition data in the expected format with date, header, meals, and totals
   * @param config - Optional configuration overrides for parsing behavior
   * @returns ParseResult containing either successful data with warnings or failure with error
   *
   * @example
   * ```typescript
   * const result = parser.parse(nutritionData, {
   *   strictMode: false,
   *   validateTotals: true
   * })
   * ```
   */
  parse(rawData: string, config?: Partial<ParserConfig>): ParseResult {
    const mergedConfig: ParserConfig = { ...defaultParserConfig, ...config }
    const warnings: string[] = []

    try {
      if (!rawData || rawData.trim() === '') {
        throw new NutritionParseError('Empty input data')
      }

      // Auto-detect format and convert if needed
      let processedData = rawData
      if (MarkdownTableParser.isMarkdownFormat(rawData)) {
        processedData = this.markdownTableParser.convertToSpaceSeparated(rawData)
        warnings.push('Converted from Markdown table format')
      }

      const lines = processedData.split('\n').map((line) => line.trim())
      const nonEmptyLines = lines.filter((line) => line.length > 0)

      if (nonEmptyLines.length === 0) {
        throw new NutritionParseError('Empty input data')
      }

      // Parse date (first line) - safe after length check
      const firstLine = nonEmptyLines.at(0)
      if (!firstLine) {
        throw new NutritionParseError('Missing date line')
      }
      const date = this.parseDate(firstLine)

      // Find header line
      const headerLineIndex = nonEmptyLines.findIndex(
        (line) => line.startsWith('FOODS') && line.includes('Calories')
      )
      if (headerLineIndex === -1) {
        throw new NutritionParseError('Missing nutrition data header')
      }

      // Find TOTALS line
      const totalsLineIndex = nonEmptyLines.findIndex((line) => line.startsWith('TOTALS'))
      if (totalsLineIndex === -1) {
        throw new MissingTotalsError()
      }

      // Parse totals - safe after findIndex validation
      const totalsLine = nonEmptyLines.at(totalsLineIndex)
      if (!totalsLine) {
        throw new MissingTotalsError()
      }
      const totals = this.parseTotalsLine(totalsLine)

      // Parse meals between header and totals
      const mealLines = nonEmptyLines.slice(headerLineIndex + 1, totalsLineIndex)
      const meals = this.parseMeals(mealLines, mergedConfig, warnings)

      // Validate totals if configured
      if (mergedConfig.validateTotals) {
        const isValid = validateTotals(meals, totals, mergedConfig.tolerancePercent)
        if (!isValid) {
          if (mergedConfig.strictMode) {
            throw new NutritionParseError('Totals validation failed')
          } else {
            warnings.push('Totals validation failed but continuing in non-strict mode')
          }
        }
      }

      const dailyNutrition: DailyNutrition = {
        date,
        meals,
        totals,
      }

      const result: ParseSuccess = {
        success: true,
        data: dailyNutrition,
        warnings,
      }

      return result
    } catch (error) {
      const parseError =
        error instanceof NutritionParseError
          ? error
          : new NutritionParseError(
              error instanceof Error ? error.message : 'Unknown parsing error'
            )

      const result: ParseFailure = {
        success: false,
        error: parseError,
      }

      return result
    }
  }

  /**
   * Parses date from various formats used in nutrition data.
   *
   * @param dateString - Date string in formats like "Sep 1, 2025", "January 15, 2024"
   * @returns Parsed date object in UTC
   * @throws InvalidDateError When date format is invalid or empty
   *
   * @example
   * ```typescript
   * const date = parser.parseDate('Sep 1, 2025')
   * // Returns: Date object for September 1, 2025
   * ```
   */
  parseDate(dateString: string): Date {
    if (!dateString || dateString.trim() === '') {
      throw new InvalidDateError(dateString)
    }

    // Reject ISO format dates (YYYY-MM-DD) - only accept natural language formats
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString.trim())) {
      throw new InvalidDateError(dateString)
    }

    // Expected formats: "Sep 1, 2025", "January 15, 2024", etc.
    // Parse as UTC to avoid timezone issues
    const date = new Date(dateString + ' 00:00:00 UTC')

    if (isNaN(date.getTime())) {
      throw new InvalidDateError(dateString)
    }

    return date
  }

  /**
   * Parses individual food item with nutrition data.
   *
   * @param foodLine - Food description line containing name, brand (optional), and quantity
   * @param nutritionValues - Array of 8 nutrition values (calories, carbs, fat, protein, cholesterol, sodium, sugar, fiber)
   * @returns Parsed food item with structured data including category
   * @throws InvalidFoodItemError When food item format is invalid or nutrition values are malformed
   *
   * @example
   * ```typescript
   * const foodItem = parser.parseFoodItem(
   *   'IGA 2% Milk - 2% Milk, 350 milliliter',
   *   ['178', '16g', '7g', '12g', '37mg', '178mg', '16g', '0g']
   * )
   * // Returns: FoodItem with name, quantity, nutrition, and category
   * ```
   */
  parseFoodItem(foodLine: string, nutritionValues: readonly string[]): FoodItem {
    const { name, quantity, category } = this.foodItemParser.parseFoodDescription(foodLine)
    const nutrition = this.foodItemParser.parseNutritionalValues(nutritionValues)

    return {
      name,
      quantity,
      nutrition,
      category,
    }
  }

  /**
   * Parses a single meal with its food items.
   *
   * @param category - Meal category (standard like Breakfast, Lunch, Dinner, Late meal, or time-based like 00-04, 16-20)
   * @param foodLines - Array of food item lines for this meal
   * @param config - Optional parser configuration for error handling
   * @param warnings - Optional array to collect parsing warnings
   * @returns ParsedMeal containing the category and parsed food items
   *
   * @example
   * ```typescript
   * const meal = parser.parseMeal('Breakfast', [
   *   'IGA 2% Milk - 2% Milk, 350 milliliter    178    16g    7g    12g    37mg    178mg    16g    0g'
   * ])
   * ```
   */
  parseMeal(
    category: MealCategoryInput,
    foodLines: readonly string[],
    config?: ParserConfig,
    warnings?: string[]
  ): ParsedMeal {
    const foodItems: FoodItem[] = []

    for (const line of foodLines) {
      try {
        // Split by multiple spaces to separate food description from nutrition values
        const parts = line.split(/\s{4,}/).filter((part) => part.trim() !== '')
        if (parts.length < 2) {
          continue // Skip incomplete lines
        }

        const foodDescription = parts.at(0)
        if (!foodDescription) {
          continue // Skip if no food description
        }
        // The nutrition values are already split correctly by the 4+ spaces
        const nutritionValues = parts.slice(1)

        if (nutritionValues.length < 8) {
          continue // Skip if we don't have enough nutrition values
        }

        const foodItem = this.parseFoodItem(foodDescription, nutritionValues.slice(0, 8))
        foodItems.push(foodItem)
      } catch (error) {
        // Handle malformed items based on strict mode
        if (error instanceof InvalidFoodItemError) {
          if (config?.strictMode) {
            // In strict mode, throw the error immediately
            throw error
          } else if (warnings) {
            // In non-strict mode, skip and add warning
            warnings.push('Skipped malformed food item')
            continue
          }
        }
        throw error
      }
    }

    return {
      category,
      foodItems,
    }
  }

  private parseMeals(
    mealLines: readonly string[],
    config: ParserConfig,
    warnings: string[]
  ): Meal[] {
    const meals: Meal[] = []
    let currentMealCategory: MealCategoryInput | null = null
    let currentMealLines: string[] = []

    for (const line of mealLines) {
      // Check if this line is a meal category (standard or time-based)
      const mealCategory = this.identifyMealCategory(line)

      if (mealCategory) {
        // Process previous meal if exists
        if (currentMealCategory) {
          try {
            const parsedMeal = this.parseMeal(
              currentMealCategory,
              currentMealLines,
              config,
              warnings
            )
            meals.push({
              category: parsedMeal.category,
              foods: parsedMeal.foodItems,
            })
          } catch (error) {
            if (config.strictMode) {
              throw error
            } else {
              const errorMessage = error instanceof Error ? error.message : String(error)
              warnings.push(`Failed to parse meal ${currentMealCategory}: ${errorMessage}`)
            }
          }
        }

        // Start new meal
        currentMealCategory = mealCategory
        currentMealLines = []
      } else if (currentMealCategory && line.trim() !== '') {
        // Add non-empty line to current meal
        currentMealLines.push(line)
      }
    }

    // Process final meal
    if (currentMealCategory) {
      try {
        const parsedMeal = this.parseMeal(currentMealCategory, currentMealLines, config, warnings)
        meals.push({
          category: parsedMeal.category,
          foods: parsedMeal.foodItems,
        })
      } catch (error) {
        if (config.strictMode) {
          throw error
        } else {
          const errorMessage = error instanceof Error ? error.message : String(error)
          warnings.push(`Failed to parse meal ${currentMealCategory}: ${errorMessage}`)
        }
      }
    }

    return meals
  }

  private parseTotalsLine(totalsLine: string): NutritionalValue {
    // Split by multiple spaces to separate TOTALS from nutrition values
    const parts = totalsLine.split(/\s{4,}/).filter((part) => part.trim() !== '')

    if (parts.length < 2) {
      throw new NutritionParseError('Invalid TOTALS line format')
    }

    // The nutrition values are already split correctly by the 4+ spaces
    const nutritionValues = parts.slice(1)

    if (nutritionValues.length < 8) {
      throw new NutritionParseError('Invalid TOTALS line format - insufficient nutrition values')
    }

    return this.foodItemParser.parseNutritionalValues(nutritionValues.slice(0, 8))
  }
}
